import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileDetailPanel, Spinner, useIsMobile } from 'react-ui';
import { useUserScope } from '../contexts/UserScope';
import { useMapBoundingBox } from '../contexts/MapBoundingBox.tsx';
import { CloseButton } from '../components/CloseButton.tsx';
import { CloseMobileDialogFooterButton } from '../components/CloseMobileDialogFooterButton.tsx';

/**
 * Right-side slide-over hosting the public profile in user-scoped map mode.
 * Mirrors EventDetailPage's shell (desktop panel left-[390px]; mobile fixed
 * overlay over the bottom sheet). Open on `/user/:slug`; closing navigates to
 * `/user/:slug/events` (scope kept, pane closed).
 */
export function ProfileDetailPage() {
    const { slug, profile, loading, notFound, paneOpen, closeProfilePane } = useUserScope();
    const isMobile = useIsMobile();
    const mapBoundingBox = useMapBoundingBox();
    const navigate = useNavigate();

    // Render when the pane is open OR the slug doesn't resolve (so an invalid
    // /user/:slug shows a "not found" message instead of a silent empty list).
    // The pane is auto-closed when an event is selected (UserScopeEventSync), so
    // no separate event check is needed here.
    if (!slug || (!paneOpen && !notFound)) return null;

    // Closing a not-found pane exits scope; otherwise it just hides the pane.
    const close = () => (notFound ? navigate('/') : closeProfilePane());

    const mobileStyles: React.HTMLAttributes<HTMLDivElement> = isMobile
        ? {
              style: {
                  zIndex: 1000,
                  position: 'fixed',
                  top: `${mapBoundingBox.top || 0}px`,
                  left: `${mapBoundingBox.left || 0}px`,
                  width: `${mapBoundingBox.width || 0}px`,
                  maxWidth: `${mapBoundingBox.width || 0}px`,
                  height: `${mapBoundingBox.height || 0}px`,
              },
          }
        : {};

    return (
        <div
            {...mobileStyles}
            className="profile-details slide-in-left absolute h-full top-0 left-[390px] w-[690px] flex flex-col bg-white backdrop-blur-md shadow-[2px_0px_6px_2px_rgba(0,0,0,0.20)]"
        >
            <div className="close-button-container absolute z-50 justify-center items-center p-0 border-0 border-gray-200 rounded-full top-4 right-6 backdrop-blur-sm bg-black/30">
                <CloseButton onClick={close} />
            </div>

            <div className="overflow-y-scroll flex-1 pointer-events-auto">
                {notFound ? (
                    <div className="p-10 flex flex-col gap-3 justify-center items-center h-full text-center">
                        <p className="text-lg font-semibold text-gray-900">No public profile</p>
                        <p className="text-sm text-gray-500">
                            This user doesn’t have a public profile, or it’s no longer available.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className="mt-2 px-4 py-2 text-sm rounded-full bg-balloon text-white hover:opacity-90 transition-opacity"
                        >
                            Back to all events
                        </button>
                    </div>
                ) : loading || !profile ? (
                    <div className="p-10 flex justify-center items-center h-full">
                        <Spinner />
                    </div>
                ) : (
                    <ProfileDetailPanel profile={profile} />
                )}
            </div>

            {isMobile && (
                <div className="flex flex-row items-center px-4 py-2 w-full border-t border-gray-200">
                    <CloseMobileDialogFooterButton onClick={close} />
                </div>
            )}
        </div>
    );
}

export default ProfileDetailPage;
